import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  FocusableComponentLayout,
  FocusContext,
  setFocus,
} from '@noriginmedia/norigin-spatial-navigation'
import {
  isValidDistributorForInAppDonations,
  platformTransitionStyle,
  throttle,
} from '../../../../shared/helpers'
import fallbackLogo from '../../pbs-logo.svg'
import googleAnalytics4 from '../../lib/googleAnalytics'
import Spinner from '../../components/Spinner'
import { useAppSelector } from '../../hooks/useStore'
import { useLocationHistory } from '../../../../shared/providers/LocationHistoryProvider'
import SettingsScreenModal from './SettingsScreenModal'
import { getPassportUrl } from './utils'
import useDonationStatus from './hooks/useDonationStatus'
import SettingsAbout from './SettingsAbout'
import SettingsAccount from './SettingsAccount'
import SettingsChangeStation from './SettingsChangeStation'
import SettingsVideo from './SettingsVideo'
import { useSidePanel } from '../../components/SidePanel/SidePanelProvider'
import { useHybridFocusable } from '../../hooks/useHybridFocusable'
import useGetModalData from './hooks/useGetModalData'
import {
  getClosedCaptions,
  getPrivacyPolicy,
  getTermsOfUse,
} from '../../actions/contentActions'

const COLLECTIONS_SET_PADDING_TOP = 236
const CHANGE_STATION_FOCUS_KEY = 'changeStationPanel'
const PASSPORT_ACTIVATE_FOCUS_KEY = 'passportPanelActivate'
declare const COMCAST_IN_APP_DONATIONS_ENABLED: boolean
declare const COMPILING_FOR_COMCASTX1: boolean

const SettingsScreen = () => {
  const {
    callSign,
    distributor,
    passportEligible,
    shortCommonName,
    stationHasDonationCampaign,
    stationLogo,
    upNextEnabled,
    user,
  } = useAppSelector((state) => ({
    callSign: state?.station.call_sign,
    distributor: state?.device?.distributor,
    passportEligible: state?.station.passportEligible,
    shortCommonName: state?.station.short_common_name,
    stationHasDonationCampaign:
      state?.station.donation_campaign.station_has_donation_campaign,
    stationLogo: state.station.white_cobranded_logo || fallbackLogo,
    upNextEnabled: state?.content.upNextEnabled,
    user: state?.user,
  }))
  const [translateY, setTranslateY] = useState(0)
  const [modalType, setModalType] = useState<
    'privacy' | 'terms' | 'captions' | null
  >(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const { ref, focusKey } = useHybridFocusable({})
  const { setKeepSidePanelOpened } = useSidePanel()
  const [fromPath] = useLocationHistory()
  const donationFeatureEnabled =
    COMCAST_IN_APP_DONATIONS_ENABLED && COMPILING_FOR_COMCASTX1
  const canDonate = useMemo(
    () =>
      donationFeatureEnabled &&
      stationHasDonationCampaign &&
      isValidDistributorForInAppDonations(distributor),
    [donationFeatureEnabled, stationHasDonationCampaign, distributor]
  )
  const { isActiveDonation, unactivatedPassport } = useDonationStatus({
    callSign,
    enabled: donationFeatureEnabled,
  })
  const passportUrl = useMemo(
    () => getPassportUrl(canDonate, user, unactivatedPassport, callSign),
    [canDonate, user, unactivatedPassport, callSign]
  )

  const modalDataMap = {
    privacy: useGetModalData({
      callback: getPrivacyPolicy,
      modalType: 'Privacy Policy',
    }),
    terms: useGetModalData({
      callback: getTermsOfUse,
      modalType: 'Terms of Use',
    }),
    captions: useGetModalData({
      callback: getClosedCaptions,
      modalType: 'Closed Captioning',
    }),
  }

  const loadingPrivacy = modalDataMap.privacy.loading
  const loadingTerms = modalDataMap.terms.loading
  const loadingCaptions = modalDataMap.captions.loading

  const modalTitle = modalType ? modalDataMap[modalType].title : ''
  const modalContent = modalType ? modalDataMap[modalType].text : ''

  useEffect(() => {
    googleAnalytics4.sendPageViewEvent('Settings')
  }, [])

  useEffect(() => {
    if (!fromPath || isModalOpen) return

    let focusTarget: string | null = null

    if (fromPath === '/change-station') {
      focusTarget = CHANGE_STATION_FOCUS_KEY
    } else if (fromPath.startsWith('/passport-upsell')) {
      const callSignFromPath = fromPath
        .split('/passport-upsell/')[1]
        ?.split('?')[0]

      if (callSignFromPath === callSign) {
        focusTarget = PASSPORT_ACTIVATE_FOCUS_KEY
      } else {
        const otherMemberships =
          user?.memberships?.filter(
            (membership) =>
              membership.call_sign !== callSign && membership.mvod_enabled
          ) || []

        const membershipIndex = otherMemberships.findIndex(
          (membership) => membership.call_sign === callSignFromPath
        )

        if (membershipIndex >= 0) {
          focusTarget = `passportPanel-${membershipIndex}`
        }
      }
    }

    if (!focusTarget) return

    if (typeof window === 'undefined') {
      setFocus(focusTarget)
      return
    }

    const timeoutId = window.setTimeout(() => setFocus(focusTarget), 0)

    return () => {
      window.clearTimeout(timeoutId)
    }
  }, [fromPath, callSign, user?.memberships, isModalOpen])

  useEffect(() => {
    if (!isModalOpen) {
      setKeepSidePanelOpened(true)
    } else {
      setKeepSidePanelOpened(false)
    }
    return () => setKeepSidePanelOpened(false)
  }, [isModalOpen])

  const handleFocus = useCallback(
    (layout: FocusableComponentLayout) => {
      setTranslateY(-(layout.top - COLLECTIONS_SET_PADDING_TOP))
    },
    [setTranslateY]
  )

  const handleCloseModal = () => {
    googleAnalytics4.sendPageViewEvent('Settings')
    setIsModalOpen(false)
  }

  const handleWheel = useMemo(
    () =>
      throttle((event: React.WheelEvent<HTMLDivElement>) => {
        const deltaY = event.deltaY
        if (deltaY > 0) {
          setTranslateY((prev) =>
            Math.max(-ref.current.getBoundingClientRect().height, prev - 300)
          )
        } else if (deltaY < 0) {
          setTranslateY((prev) => Math.min(0, prev + 300))
        }
      }, 600),
    []
  )

  const openModalWithType = (modalType: string) => {
    setIsModalOpen(true)
    setModalType(modalType as 'privacy' | 'terms' | 'captions')
  }

  const renderSpinner = loadingCaptions || loadingPrivacy || loadingTerms
  return (
    <FocusContext.Provider value={focusKey}>
      {isModalOpen && renderSpinner && (
        <Spinner className="settings-screen-spinner" />
      )}
      {isModalOpen && (
        <SettingsScreenModal
          title={modalTitle}
          content={modalContent}
          onClose={handleCloseModal}
        />
      )}
      <div
        style={{
          transform: `translateY(${translateY}px)`,
          transition: platformTransitionStyle('transform 400ms ease-in-out'),
        }}
        onWheel={handleWheel}
      >
        <div className="settings-screen-container" ref={ref}>
          <SettingsChangeStation
            callSign={callSign}
            handleFocus={handleFocus}
            shortCommonName={shortCommonName}
            stationLogo={stationLogo}
            passportEligible={passportEligible}
            passportUrl={passportUrl}
            user={user}
          />
          <SettingsVideo
            upNextEnabled={upNextEnabled}
            handleFocus={handleFocus}
          />
          <SettingsAbout
            handleFocus={handleFocus}
            setModalType={openModalWithType}
            isModalOpen={isModalOpen}
          />
          <SettingsAccount
            donationFeatureEnabled={donationFeatureEnabled}
            isActiveDonation={isActiveDonation}
            handleFocus={handleFocus}
            user={user}
          />
        </div>
      </div>
    </FocusContext.Provider>
  )
}

export default SettingsScreen
